// Integração da Captação com o IW (exportação TDEXP_ENTRADA, a cada 10 min):
//  - BotaoImportacaoIW: lista os "Em avaliação" do IW que ainda não estão na Captação
//    (para conferir e criar o card) e mostra se a importação automática está ligada;
//  - InfoCadastroIW: linha informativa no card ("Cadastrado no IW às ..."; também o
//    selo dos cards que vieram por e-mail — api/_captacao-email-importar.js).
// O tempo da captação continua contado só pelos horários do Quality.
import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';

let cache = null; // { em, dados } compartilhado entre o botão e os cards
let pedido = null;
const ouvintes = new Set();
const CACHE_MS = 5 * 60 * 1000;

async function chamar(metodo = 'GET', corpo) {
  const { data: { session } } = await supabase.auth.getSession();
  const resp = await fetch('/api/captacao-iw', {
    method: metodo,
    headers: { Authorization: `Bearer ${session?.access_token || ''}`, 'Content-Type': 'application/json' },
    body: corpo ? JSON.stringify(corpo) : undefined,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(json.error || `Erro ${resp.status}`);
  return json;
}

function carregar(forcar = false) {
  if (!forcar && cache && Date.now() - cache.em < CACHE_MS) return Promise.resolve(cache.dados);
  if (pedido) return pedido;
  pedido = chamar()
    .then(dados => { cache = { em: Date.now(), dados }; ouvintes.forEach(fn => fn(dados)); return dados; })
    .finally(() => { pedido = null; });
  return pedido;
}

function useDadosIW() {
  const [dados, setDados] = useState(cache?.dados || null);
  useEffect(() => {
    ouvintes.add(setDados);
    carregar().then(setDados).catch(() => {});
    const id = setInterval(() => carregar().catch(() => {}), CACHE_MS);
    return () => { ouvintes.delete(setDados); clearInterval(id); };
  }, []);
  return dados;
}

const fmt = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export function InfoCadastroIW({ paciente }) {
  const dados = useDadosIW();
  const iso = paciente?.iwCriadoEm || (paciente?.numIW && dados?.horarios?.[String(paciente.numIW).trim()]);
  const veioDoIW = paciente?.origem === 'IW';
  const veioDoEmail = paciente?.origem === 'Email';
  const pend = paciente?.pendenteCompletar || [];
  const falta = (veioDoIW || veioDoEmail) && paciente.status === 'Recebido'
    ? [!paciente.hospital && 'hospital', pend.includes('vm') && 'VM', veioDoEmail && !paciente.dataNascimento && 'nascimento'].filter(Boolean) : [];
  const revisar = veioDoEmail && paciente.status === 'Recebido' ? (paciente.revisar || []) : [];
  if (!iso && !veioDoIW && !veioDoEmail) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 3 }}>
      {veioDoEmail && (
        <span title={`Pedido recebido por e-mail de ${paciente.emailRemetente || ''}`}
          style={{ fontSize: '0.64rem', color: '#1e40af', fontWeight: 700, background: '#eff6ff', borderRadius: 5, padding: '2px 6px' }}>
          📧 Veio do e-mail · {fmt(paciente.emailRecebidoEm)}{(paciente.anexos || []).length > 0 && ` · 📎 ${paciente.anexos.length}`}
        </span>
      )}
      {revisar.length > 0 && (
        <span title={revisar.join(' · ')}
          style={{ fontSize: '0.64rem', color: '#991b1b', fontWeight: 700, background: '#fee2e2', borderRadius: 5, padding: '2px 6px' }}>
          ⚠ revisar
        </span>
      )}
      {iso && (
        <span title="Informativo: não entra no cálculo do tempo de captação"
          style={{ fontSize: '0.64rem', color: '#065f46', fontWeight: 700, background: '#ecfdf5', borderRadius: 5, padding: '2px 6px' }}>
          🗂 {veioDoIW ? 'Veio do IW' : 'No IW'} · {fmt(iso)}
        </span>
      )}
      {falta.length > 0 && (
        <span style={{ fontSize: '0.64rem', color: '#9a3412', fontWeight: 700, background: '#fff7ed', borderRadius: 5, padding: '2px 6px' }}>
          completar: {falta.join(', ')}
        </span>
      )}
    </div>
  );
}

export function BotaoImportacaoIW({ podeCriar }) {
  const dados = useDadosIW();
  const [aberto, setAberto] = useState(false);
  const [criando, setCriando] = useState('');
  const [erro, setErro] = useState('');
  const pendentes = (dados?.itens || []).filter(i => i.situacao !== 'existente');

  const criar = async item => {
    setCriando(item.numIW); setErro('');
    try {
      await chamar('POST', { numIW: item.numIW });
      await carregar(true);
    } catch (e) { setErro(`${item.nome}: ${e.message}`); }
    setCriando('');
  };

  if (!dados) return null;
  return (
    <>
      <button onClick={() => setAberto(true)} title="Pacientes 'Em avaliação' no IW que ainda não estão na Captação"
        style={{ border: '1px solid #a7f3d0', background: '#ecfdf5', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, color: '#065f46', display: 'flex', alignItems: 'center', gap: 6 }}>
        🗂 IW{pendentes.length > 0 && <span style={{ background: '#059669', color: '#fff', borderRadius: 999, padding: '0 7px', fontSize: '0.72rem' }}>{pendentes.length}</span>}
      </button>
      {aberto && (
        <div role="dialog" aria-labelledby="titulo-iw" onClick={() => setAberto(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 760, maxHeight: '85vh', overflow: 'auto', padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
              <div>
                <div id="titulo-iw" style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1e293b' }}>Pacientes "Em avaliação" no IW</div>
                <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                  Exportação do IW de {fmt(dados.atualizadoEm) || '—'} · {(dados.itens || []).length} em avaliação · {dados.itens.length - pendentes.length} já estão na Captação
                </div>
              </div>
              <button onClick={() => setAberto(false)} aria-label="Fechar" style={{ border: 0, background: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <div style={{ fontSize: '0.8rem', borderRadius: 10, padding: '8px 12px', margin: '10px 0 14px', background: dados.automatico ? '#ecfdf5' : '#f1f5f9', color: dados.automatico ? '#065f46' : '#334155' }}>
              {dados.automatico
                ? <>Importação automática <strong>ligada</strong>: atendimentos criados no IW a partir de {fmt(dados.inicioAutomatico)} viram card em "Recebido" sozinhos, a cada 10 min.</>
                : <>Importação automática <strong>desligada</strong> (modo conferência): crie os cards manualmente abaixo.</>}
            </div>
            {erro && <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem', marginBottom: 10 }}>{erro}</div>}
            {pendentes.length === 0
              ? <div style={{ textAlign: 'center', color: '#64748b', padding: 24, fontSize: '0.9rem' }}>Todos os "Em avaliação" do IW já estão na Captação.</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      {['Paciente', 'Nº IW', 'Convênio (IW)', 'Unidade', 'Criado no IW', ''].map(h => <th key={h} style={{ padding: '8px 6px' }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {pendentes.map(i => (
                      <tr key={i.numIW} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '9px 6px', fontWeight: 700 }}>{i.nome}</td>
                        <td style={{ padding: '9px 6px', color: '#475569' }}>{i.numIW}</td>
                        <td style={{ padding: '9px 6px' }}>{i.convenioIW || '—'}</td>
                        <td style={{ padding: '9px 6px' }}>{i.unidade}</td>
                        <td style={{ padding: '9px 6px' }}>{fmt(i.criadoNoIW)}{i.situacao === 'conferir' && <div style={{ fontSize: '0.7rem', color: '#9a3412' }}>anterior à importação automática</div>}</td>
                        <td style={{ padding: '9px 6px', textAlign: 'right' }}>
                          {podeCriar
                            ? <button onClick={() => criar(i)} disabled={criando === i.numIW}
                                style={{ border: 0, background: '#4f46e5', color: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                {criando === i.numIW ? 'Criando…' : 'Criar card'}
                              </button>
                            : <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>sem permissão</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </div>
      )}
    </>
  );
}
